import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Check } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AppContext";
import { formatDate, romeDateKey } from "@/lib/format";
import { AdminTitle } from "./AdminLayout";

const WD = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];
const NEW_WINDOW = { start: "09:00", end: "13:00", treatment_ids: [], staff_ids: [] };

function MonthGrid({ month, setMonth, days, selected, onSelect }) {
  const cells = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const offset = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
    const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return [...Array(offset).fill(null), ...Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  }, [month]);
  const shift = (n) => { const [y, m] = month.split("-").map(Number); setMonth(new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7)); };
  const label = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
  return (
    <div className="card-soft p-5" data-testid="opening-calendar">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => shift(-1)} aria-label="Mese precedente" data-testid="opening-prev"><ChevronLeft /></button>
        <p className="font-serif text-2xl capitalize">{label}</p>
        <button onClick={() => shift(1)} aria-label="Mese successivo" data-testid="opening-next"><ChevronRight /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-[var(--muted-ink)]">{WD.map((d) => <span key={d} className="py-1">{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const od = days[d];
          const past = d < romeDateKey();
          return (
            <button key={d} disabled={past && !od} onClick={() => onSelect(d)} aria-pressed={selected === d} aria-label={`${d}${od ? " aperto" : " chiuso"}`}
              className={`flex aspect-square flex-col items-center justify-center rounded-xl text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${past ? "opacity-50" : ""} ${selected === d ? "ring-2 ring-[var(--brass)]" : ""} ${od ? "bg-[var(--petrol)] font-semibold text-white" : "bg-white hover:bg-[var(--ivory)]"}`} data-testid={`opening-day-${d}`}>
              {Number(d.slice(8))}
              {od && <span className="text-[10px] font-normal opacity-80">{od.windows.length} fasc{od.windows.length === 1 ? "ia" : "e"}</span>}
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-[var(--muted-ink)]">In petrolio i giorni di apertura. Clicca un giorno per aprirlo o modificarlo.</p>
    </div>
  );
}

function WindowRow({ w, onChange, onRemove, treatments, staff }) {
  const toggle = (key, id) => onChange({ ...w, [key]: w[key].includes(id) ? w[key].filter((x) => x !== id) : [...w[key], id] });
  return (
    <div className="grid gap-3 rounded-xl border border-[var(--line)] bg-white p-4" data-testid="opening-window">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label>Dalle <input type="time" step={300} className="field !w-auto !py-1.5" value={w.start} onChange={(e) => onChange({ ...w, start: e.target.value })} data-testid="opening-window-start" /></label>
        <label>alle <input type="time" step={300} className="field !w-auto !py-1.5" value={w.end} onChange={(e) => onChange({ ...w, end: e.target.value })} data-testid="opening-window-end" /></label>
        <button onClick={onRemove} className="ml-auto text-red-700" aria-label="Rimuovi fascia" data-testid="opening-window-remove"><Trash2 size={16} /></button>
      </div>
      <div>
        <p className="text-xs text-[var(--muted-ink)]">Trattamenti prenotabili in questa fascia {w.treatment_ids.length === 0 && <strong>(nessuno selezionato = tutti)</strong>}</p>
        <div className="mt-2 flex flex-wrap gap-2">{treatments.map((t) => (
          <label key={t.id} className={`flex cursor-pointer items-center gap-1 rounded-full border px-3 py-1 text-xs ${w.treatment_ids.includes(t.id) ? "border-[var(--petrol)] bg-[var(--petrol)] text-white" : "border-[var(--line)]"}`}>
            <input type="checkbox" className="sr-only" checked={w.treatment_ids.includes(t.id)} onChange={() => toggle("treatment_ids", t.id)} data-testid={`opening-window-treatment-${t.slug}`} />{w.treatment_ids.includes(t.id) && <Check size={12} />}{t.name} · {t.duration_min}′
          </label>))}</div>
      </div>
      {staff.length > 1 && (
        <div><p className="text-xs text-[var(--muted-ink)]">Professionisti presenti (nessuno = tutti)</p>
          <div className="mt-2 flex flex-wrap gap-2">{staff.map((s) => <label key={s.id} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={w.staff_ids.includes(s.id)} onChange={() => toggle("staff_ids", s.id)} />{s.name}</label>)}</div></div>
      )}
    </div>
  );
}

function DayEditor({ date, existing, treatments, staff, onSaved, canWrite }) {
  const [windows, setWindows] = useState(existing?.windows || [NEW_WINDOW]);
  const [note, setNote] = useState(existing?.note || "");
  const [copyTo, setCopyTo] = useState("");
  const save = async (targetDate = date, target = existing) => {
    const body = { date: targetDate, windows, note, order: 0 };
    try {
      if (target) await api.put(`/admin/opening_days/${target.id}`, body); else await api.post("/admin/opening_days", body);
      toast.success(`Giorno ${targetDate} salvato`); onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Chiudere questo giorno? Gli appuntamenti già presi restano validi e visibili in agenda.")) return;
    try { await api.delete(`/admin/opening_days/${existing.id}`); toast.success("Giorno chiuso"); onSaved(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="card-soft grid gap-4 p-6" data-testid="opening-editor">
      <h2 className="text-2xl capitalize">{formatDate(`${date}T12:00:00Z`)}</h2>
      <p className="text-sm text-[var(--muted-ink)]">{existing ? "Giorno aperto." : "Giorno chiuso: aggiungi le fasce orarie per aprirlo."} Gli orari proposti ai clienti si calcolano in base alla durata di ciascun trattamento.</p>
      {windows.map((w, i) => (
        <WindowRow key={i} w={w} treatments={treatments} staff={staff} onChange={(nw) => setWindows(windows.map((x, j) => (j === i ? nw : x)))} onRemove={() => setWindows(windows.filter((_, j) => j !== i))} />
      ))}
      {canWrite && <>
        <button className="flex w-fit items-center gap-1 text-sm text-[var(--petrol)]" onClick={() => setWindows([...windows, { ...NEW_WINDOW, start: "14:00", end: "18:00" }])} data-testid="opening-add-window"><Plus size={16} /> Aggiungi fascia</button>
        <label className="text-sm">Nota interna<input className="field mt-1" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} /></label>
        <div className="flex flex-wrap gap-3">
          <button className="btn-primary" disabled={!windows.length} onClick={() => save()} data-testid="opening-save">{existing ? "Salva modifiche" : "Apri questo giorno"}</button>
          {existing && <button className="btn-outline !border-red-700 !text-red-700" onClick={remove} data-testid="opening-delete">Chiudi giorno</button>}
        </div>
        <div className="flex flex-wrap items-end gap-2 border-t border-[var(--line)] pt-4">
          <label className="text-sm">Copia le stesse fasce sul giorno<input type="date" className="field mt-1" value={copyTo} onChange={(e) => setCopyTo(e.target.value)} data-testid="opening-copy-date" /></label>
          <button className="btn-outline !py-2 text-sm" disabled={!copyTo || !windows.length} onClick={() => save(copyTo, null)} data-testid="opening-copy-btn">Copia</button>
        </div>
      </>}
    </div>
  );
}

export default function OpeningDays() {
  const { can } = useAuth();
  const [month, setMonth] = useState(romeDateKey().slice(0, 7));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refs, setRefs] = useState({ treatments: [], staff: [] });
  const [mode, setMode] = useState(null);
  const load = () => api.get("/admin/opening_days").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
    Promise.all([api.get("/admin/treatments"), api.get("/admin/staff")]).then(([t, s]) => setRefs({ treatments: t.data.filter((x) => x.status !== "archived" && x.bookable), staff: s.data.filter((x) => x.active) })).catch(() => {});
    api.get("/admin/settings/booking").then((r) => setMode(r.data.schedule_mode || "weekly")).catch(() => {});
  }, []);
  const byDate = Object.fromEntries(items.map((d) => [d.date, d]));
  return (
    <>
      <AdminTitle title="Giorni di apertura" />
      {mode === "weekly" && <p className="mb-6 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Attenzione: in Impostazioni → Prenotazioni è attivo l'orario settimanale fisso. Per usare questi giorni seleziona "Solo giorni di apertura scelti".</p>}
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <MonthGrid month={month} setMonth={setMonth} days={byDate} selected={selected} onSelect={setSelected} />
        {selected ? <DayEditor key={selected + (byDate[selected]?.updated_at || "")} date={selected} existing={byDate[selected]} treatments={refs.treatments} staff={refs.staff} canWrite={can("schedule.write")} onSaved={load} />
          : <p className="text-sm text-[var(--muted-ink)]">Seleziona un giorno nel calendario.</p>}
      </div>
    </>
  );
}
